import { lazy, useRef, Suspense, useState, useEffect, useCallback } from 'react';

const DebugAdminDialog = lazy(() => import('./DebugAdminDialog'));

// ----------------------------------------------------------------------

const DEBUG_TAP_COUNT = 3;
const DEBUG_TAP_WINDOW_MS = 2000;
const HEADER_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]';

type Props = {
  triggerSelector?: string;
  requiredTapCount?: number;
  tapWindowMs?: number;
};

/**
 * Adds the hidden system-page escape hatch without changing page layouts.
 * Repeated taps on the same matching target open the existing admin debug
 * panel, which includes logout and recovery controls. Production access is
 * code-gated. By default, three taps on visible headings are required.
 */
export default function SystemPageDebugAccess({
  triggerSelector = HEADER_SELECTOR,
  requiredTapCount = DEBUG_TAP_COUNT,
  tapWindowMs = DEBUG_TAP_WINDOW_MS,
}: Props) {
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const tapCountRef = useRef(0);
  const tappedTargetRef = useRef<Element | null>(null);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTapSequence = useCallback(() => {
    tapCountRef.current = 0;
    tappedTargetRef.current = null;

    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
    }
  }, []);

  const handleTargetTap = useCallback(
    (event: MouseEvent) => {
      const { target } = event;
      if (!(target instanceof Element)) return;

      const tappedTarget = target.closest(triggerSelector);
      if (!tappedTarget) return;

      if (tappedTargetRef.current !== tappedTarget) {
        resetTapSequence();
        tappedTargetRef.current = tappedTarget;
      }

      tapCountRef.current += 1;

      if (tapCountRef.current >= requiredTapCount) {
        resetTapSequence();
        setDebugDialogOpen(true);
        return;
      }

      if (!tapTimerRef.current) {
        tapTimerRef.current = setTimeout(resetTapSequence, tapWindowMs);
      }
    },
    [requiredTapCount, resetTapSequence, tapWindowMs, triggerSelector]
  );

  useEffect(() => {
    document.addEventListener('click', handleTargetTap);

    return () => {
      document.removeEventListener('click', handleTargetTap);
      resetTapSequence();
    };
  }, [handleTargetTap, resetTapSequence]);

  if (!debugDialogOpen) return null;

  return (
    <Suspense fallback={null}>
      <DebugAdminDialog open onClose={() => setDebugDialogOpen(false)} />
    </Suspense>
  );
}
